import React, { useEffect, useMemo, useState } from "react"
import {
  Alert,
  Button,
  Container,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Table,
} from "reactstrap"
import Swal from "sweetalert2"

import { Loading } from "components"
import ListHeader from "ui-componets/list-header"
import ListFilterBar from "ui-componets/list-filter-bar"
import { lenguage, dateConfig } from "config/format-date"
import ModalNewBilling from "./ModalNewBilling"
import ModalSendBillingMail from "./ModalSendBillingMail"
import ModalTicket from "./ModalTicket"
import "assets/scss/custom/components/table-interactive.scss"
import "assets/scss/custom/components/billing-table.scss"
import {
  getTicketInvoices,
  timbrarTicketInvoice,
  downloadInvoiceXML,
  downloadInvoicePDF,
  getInvoiceAcuse,
  cancelTicketInvoice,
  getTicketInvoiceEmailHistory,
} from "services/ticket-invoices"
import { getTicket, sendEmailInvoice } from "services/tickets"
import { useQuery } from "@tanstack/react-query"
import { getDateFilterParams } from "utils/date-filters"

const getSingleFilterValue = (value) => {
  if (Array.isArray(value)) {
    return value.length === 1 ? value[0] : "all"
  }

  return value
}

const getInvoiceLabel = (invoice) => {
  if (invoice?.serie && invoice?.folio) {
    return `${invoice.serie}-${invoice.folio}`
  }

  return invoice?.folio || invoice?.number_invoice || invoice?.id || "N/A"
}

const BillingList = () => {
  const [isLoading, setIsLoading] = useState(false)
  const [searchTxt, setSearchTxt] = useState("")
  const [searchTerm, setSearchTerm] = useState("")
  const [dateFilter, setDateFilter] = useState({ preset: "", startDate: "", endDate: "" })
  const [invoiceStatus, setInvoiceStatus] = useState("all")
  const [currentPage, setCurrentPage] = useState(1)
  const [showErrorAlert, setShowErrorAlert] = useState(false)
  const [downloadingInvoice, setDownloadingInvoice] = useState(null)
  const [invoicePreview, setInvoicePreview] = useState({
    isOpen: false,
    url: "",
    id: null,
    label: "",
  })
  
  // Estado y funciones para el modal de nueva factura
  const [modalNewBilling, setModalNewBilling] = useState(false)
  const toggleModalNewBilling = () => {
    setModalNewBilling(!modalNewBilling)
  }

  // Estado y funciones para el modal del ticket
  const [modalTicket, setModalTicket] = useState(false)
  const [selectedTicket, setSelectedTicket] = useState(null)
  const toggleModalTicket = () => {
    setModalTicket(!modalTicket)
    if (!modalTicket === false) {
      setSelectedTicket(null)
    }
  }

  // Estado y funciones para el modal de envío de factura por correo
  const [modalSendMail, setModalSendMail] = useState(false)
  const [selectedInvoiceMail, setSelectedInvoiceMail] = useState(null)
  const openSendMailModal = (invoice) => {
    const ticketId = invoice?.tickets?.[0]?.id

    if (!ticketId) {
      Swal.fire({
        title: "No disponible",
        text: "No se encontró un ticket asociado para enviar la factura por correo.",
        icon: "error",
      })
      return
    }

    const customerEmail = invoice?.customer_email?.trim()
    const prefilledEmails = customerEmail ? [customerEmail] : [""]

    setSelectedInvoiceMail({
      id: invoice.id,
      uuid: invoice.uuid,
      ticketId,
      folio: invoice.serie && invoice.folio
        ? `${invoice.serie}-${invoice.folio}`
        : (invoice.folio || invoice.number_invoice || invoice.id),
      prefilledEmails,
    })
    setModalSendMail(true)
  }

  const closeSendMailModal = () => {
    setModalSendMail(false)
    setSelectedInvoiceMail(null)
  }

  const closeInvoicePreview = () => {
    if (invoicePreview.url) {
      window.URL.revokeObjectURL(invoicePreview.url)
    }

    setInvoicePreview({
      isOpen: false,
      url: "",
      id: null,
      label: "",
    })
  }

  useEffect(() => {
    return () => {
      if (invoicePreview.url) {
        window.URL.revokeObjectURL(invoicePreview.url)
      }
    }
  }, [invoicePreview.url])

  const PAGE_SIZE = 30

  const queryFilters = useMemo(
    () => {
      const invoiceStatusValue = getSingleFilterValue(invoiceStatus)

      return {
        offset: (currentPage - 1) * PAGE_SIZE,
        per_page: PAGE_SIZE,
        ...(searchTerm ? { search: searchTerm } : {}),
        ...getDateFilterParams(dateFilter, "invoice_date"),
        ...(invoiceStatusValue !== "all" ? { status: invoiceStatusValue } : {}),
        ordering: "-invoice_date",
      }
    },
    [currentPage, dateFilter, invoiceStatus, searchTerm]
  )

  const invoicesQuery = useQuery(
    ["invoices", queryFilters],
    () => getTicketInvoices(queryFilters),
    {
      keepPreviousData: true,
    }
  )
  const invoices = invoicesQuery
  const { refetch } = invoicesQuery

  const invoiceEmailHistoryQuery = useQuery(
    ["ticket-invoice-email-history", selectedInvoiceMail?.id],
    () => getTicketInvoiceEmailHistory(selectedInvoiceMail.id),
    {
      enabled: modalSendMail && Boolean(selectedInvoiceMail?.id),
    }
  )
  
  const StyleTableHeaders = {
    position: "sticky",
    background: "white", 
    top: "0px",
    zIndex: "3",
    textAlign: "center",
    verticalAlign: "middle",
  }

  const styleCenterTable = { 
    textAlign: "center", 
    verticalAlign: "middle" 
  }

  // Formatear moneda
  function currencyFormatter({ currency, value }) {
    const formatter = new Intl.NumberFormat("en-US", {
      style: "currency",
      minimumFractionDigits: 2,
      currency,
    })
    return formatter.format(value)
  }

  // Formatear fecha
  const formatDate = (dateString) => {
    let date
    if (dateString.includes("T") && dateString.includes("Z")) {
      // Formato ISO con timezone
      date = new Date(dateString.replace("Z", ""))
    } else if (dateString.includes(" ")) {
      // Formato (YYYY-MM-DD HH:MM:SS)
      date = new Date(dateString)
    } else {
      date = new Date(dateString)
    }
    
    return date.toLocaleString(lenguage, dateConfig)
  }

  // Espera 300ms para hacer la búsqueda
  useEffect(() => {
    const timer = setTimeout(() => {
      setCurrentPage(1)
      setSearchTerm(searchTxt.trim())
    }, 300)
    return () => clearTimeout(timer)
  }, [searchTxt])

  // Botón de búsqueda
  const handleSearch = () => {
    setCurrentPage(1)
    setSearchTerm(searchTxt.trim())
  }

  const handleChangeSearch = (event) => {
    setSearchTxt(event.target.value)
  }

  const handleDateFilterChange = (nextDateFilter) => {
    setCurrentPage(1)
    setDateFilter(nextDateFilter)
  }

  const handleInvoiceStatusChange = (value) => {
    setCurrentPage(1)
    setInvoiceStatus(value)
  }

  const clearListFilters = () => {
    setCurrentPage(1)
    setDateFilter({ preset: "", startDate: "", endDate: "" })
    setInvoiceStatus("all")
  }

  const handleChangePage = (nextPage) => {
    if (nextPage < 1) {
      return
    }
    if (totalPages > 0 && nextPage > totalPages) {
      return
    }
    setCurrentPage(nextPage)
  }

  // Descargar PDF
  const handleDownloadPDF = async (billingId) => {
    try {
      setDownloadingInvoice({ id: billingId, type: "pdf" })
      const blob = await downloadInvoicePDF(billingId)
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement("a")
      link.href = url
      link.download = `factura-${billingId}.pdf`
      link.click()
      window.URL.revokeObjectURL(url)
    } catch (error) {
      setShowErrorAlert(true)
      console.error("Error al descargar PDF:", error)
    } finally {
      setDownloadingInvoice(null)
    }
  }

  const handleViewInvoicePDF = async (invoice) => {
    if (!invoice?.id) {
      return
    }

    try {
      setDownloadingInvoice({ id: invoice.id, type: "preview" })
      const blob = await downloadInvoicePDF(invoice.id)
      const url = window.URL.createObjectURL(blob)

      if (invoicePreview.url) {
        window.URL.revokeObjectURL(invoicePreview.url)
      }

      setInvoicePreview({
        isOpen: true,
        url,
        id: invoice.id,
        label: getInvoiceLabel(invoice),
      })
    } catch (error) {
      console.error("Error al cargar PDF:", error)
      Swal.fire(
        "PDF no disponible",
        "No se pudo recuperar el PDF de esta factura.",
        "error"
      )
    } finally {
      setDownloadingInvoice(null)
    }
  }

  // Descargar XML 
  const handleDownloadXML = async (billingId) => {
    try {
      setDownloadingInvoice({ id: billingId, type: "xml" })
      const blob = await downloadInvoiceXML(billingId)
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement("a")
      link.href = url
      link.download = `factura-${billingId}.xml`
      link.click()
      window.URL.revokeObjectURL(url)
    } catch (error) {
      setShowErrorAlert(true)
      console.error("Error al descargar XML:", error)
    } finally {
      setDownloadingInvoice(null)
    }
  }

  // Timbrar factura
  const handleTimbrado = async (billingId) => {
    try {
      const result = await Swal.fire({
        title: "Timbrar",
        text: "¿Deseas timbrar esta factura?",
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Sí, timbrar",
        cancelButtonText: "Cancelar",
      })

      if (result.isConfirmed) {
        setIsLoading(true)
        await timbrarTicketInvoice(billingId)
        refetch()
        Swal.fire(
          "¡Éxito!",
          "La factura ha sido timbrada correctamente",
          "success"
        )
      }
    } catch (error) {
      console.error("Error al timbrar:", error)
      Swal.fire("Error", error.message || "Error al timbrar factura", "error")
    } finally {
      setIsLoading(false)
    }
  }

  // Cancelar factura
  const handleCancel = async (billingId) => {
    try {
      const { value: reason } = await Swal.fire({
        title: "Cancelar factura",
        input: "select",
        inputLabel: "Motivo de cancelación",
        inputOptions: {
          "01": "01 - Comprobante emitido con errores con relación",
          "02": "02 - Comprobante emitido con errores sin relación",
          "03": "03 - No se llevó a cabo la operación",
          "04": "04 - Operación nominativa relacionada en factura global",
        },
        inputValue: "02",
        inputAttributes: {
          "aria-label": "Selecciona el motivo de cancelación",
        },
        showCancelButton: true,
        confirmButtonText: "Cancelar factura",
        cancelButtonText: "Cerrar",
        inputValidator: (value) => {
          if (!value) {
            return "Debes seleccionar un motivo"
          }
        },
      })

      if (reason) {
        setIsLoading(true)
        await cancelTicketInvoice(billingId, reason)
        refetch()
        Swal.fire(
          "¡Éxito!",
          "La factura ha sido cancelada correctamente",
          "success"
        )
      }
    } catch (error) {
      console.error("Error al cancelar:", error)
      Swal.fire("Error", error.message || "Error al cancelar factura", "error")
    } finally {
      setIsLoading(false)
    }
  }

  // Descargar acuse
  const handleGetAcuse = async (billingId) => {
    try {
      setIsLoading(true)
      const blob = await getInvoiceAcuse(billingId)
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement("a")
      link.href = url
      link.download = `acuse-${billingId}.xml`
      link.click()
      window.URL.revokeObjectURL(url)
    } catch (error) {
      setShowErrorAlert(true)
      console.error("Error al descargar acuse:", error)
    } finally {
      setIsLoading(false)
    }
  }

  // Ver detalles del ticket
  const handleViewTicket = async (ticketId) => {
    if (!ticketId) {
      return
    }

    try {
      const ticket = await getTicket(ticketId)
      setSelectedTicket(ticket)
      setModalTicket(true)
    } catch (error) {
      console.log("Ticket no encontrado:", ticketId, error)
      setShowErrorAlert(true)
    }
  }

  const handleSendInvoiceMail = async (emails) => {
    if (!selectedInvoiceMail?.ticketId) {
      throw new Error("No se encontró el ticket asociado a la factura.")
    }
    if (!selectedInvoiceMail?.uuid) {
      throw new Error("No se encontró el UUID de la factura.")
    }

    const [email = "", email2 = "", email3 = "", email4 = "", email5 = ""] = emails
    const payload = {
      uuid: selectedInvoiceMail.uuid,
      email,
      email2,
      email3,
      email4,
      email5,
    }

    try {
      setIsLoading(true)
      await sendEmailInvoice(selectedInvoiceMail.ticketId, payload)
      await invoiceEmailHistoryQuery.refetch()
      await Swal.fire({
        title: "Enviado",
        text: "La factura se envió correctamente por correo.",
        icon: "success",
      })
      closeSendMailModal()
    } catch (error) {
      await invoiceEmailHistoryQuery.refetch()
      const message =
        error?.response?.data?.detail ||
        error?.response?.data?.error ||
        error?.message ||
        "No se pudo enviar el correo"
      await Swal.fire({
        title: "Error",
        text: message,
        icon: "error",
      })
      throw error
    } finally {
      setIsLoading(false)
    }
  }

  const totalCount = invoices.data?.count || 0
  const totalPages = totalCount > 0 ? Math.ceil(totalCount / PAGE_SIZE) : 1
  const startItem = totalCount === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1
  const endItem = Math.min(currentPage * PAGE_SIZE, totalCount)

  return (
    <>
      {/* Modal de nueva factura */}
      <ModalNewBilling 
        isOpen={modalNewBilling} 
        toggle={toggleModalNewBilling}
        refetch={refetch}
      />

      <ModalTicket
        isOpen={modalTicket} 
        toggle={toggleModalTicket}
        selectedTicket={selectedTicket}
      />

      <ModalSendBillingMail
        isOpen={modalSendMail}
        toggle={closeSendMailModal}
        invoiceLabel={selectedInvoiceMail?.folio}
        initialEmails={selectedInvoiceMail?.prefilledEmails}
        onSend={handleSendInvoiceMail}
        isLoading={isLoading}
        emailHistory={invoiceEmailHistoryQuery.data || []}
        isHistoryLoading={invoiceEmailHistoryQuery.isLoading || invoiceEmailHistoryQuery.isFetching}
      />

      <Modal
        isOpen={invoicePreview.isOpen}
        toggle={closeInvoicePreview}
        centered={true}
        style={{ maxWidth: "800px", width: "95%" }}
      >
        <ModalHeader toggle={closeInvoicePreview}>
          {invoicePreview.label ? `Factura ${invoicePreview.label}` : "Factura"}
        </ModalHeader>
        <ModalBody style={{ padding: "0px" }}>
          {invoicePreview.url && (
            <iframe
              title={invoicePreview.label || "Factura PDF"}
              src={invoicePreview.url}
              style={{
                border: "0",
                display: "block",
                height: "72vh",
                width: "100%",
              }}
            />
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={closeInvoicePreview}>
            Cerrar
          </Button>
          <Button
            color="danger"
            onClick={() => handleDownloadPDF(invoicePreview.id)}
            disabled={!invoicePreview.id || Boolean(downloadingInvoice)}
          >
            <i className="fa fa-file-pdf me-2" />
            Descargar PDF
          </Button>
        </ModalFooter>
      </Modal>

      <div className="page-content">
        <Container fluid>
          {/* Alert de error */}
          <Alert
            color="danger"
            isOpen={showErrorAlert || invoices.isError}
            toggle={() => setShowErrorAlert(false)}
          >
            Error al cargar los datos
          </Alert>

        <h2>Lista de Facturas</h2>
        
        <div className="bg-white p-3">

          {/* Búsqueda y botón juntos */}
          <div className="d-flex align-items-center mb-3 gap-2">
            <div style={{ flex: 1 }}>
              <ListHeader 
                handleOnChangeSearch={handleChangeSearch} 
                handleSearch={handleSearch} 
                searchTxt={searchTxt} 
                filtersContent={
                  <div className="billing-filters">
                    <div className="billing-filters-date">
                      <ListFilterBar
                        dateFilter={dateFilter}
                        onDateFilterChange={handleDateFilterChange}
                      />
                    </div>
                    <div className="billing-filters-actions">
                      <ListFilterBar
                        selects={[
                          {
                            name: "invoiceStatus",
                            label: "Estado de factura",
                            icon: "fa fa-file-invoice",
                            value: invoiceStatus,
                            onChange: handleInvoiceStatusChange,
                            options: [
                              { value: "all", label: "Todas" },
                              { value: "active", label: "Activas" },
                              { value: "cancelled", label: "Canceladas" },
                            ],
                          },
                        ]}
                        onClear={clearListFilters}
                      />
                    </div>
                  </div>
                }
              />
            </div>
            <Button color="success" onClick={toggleModalNewBilling}>
              <i className="fa fa-plus"></i> Crear Factura
            </Button>
          </div>

          {/* Tabla */}
          {isLoading || invoices.isLoading ? (
            <div className="my-5 text-center">
              <Loading isLoading={isLoading || invoices.isLoading} size={2} />
            </div>
          ) : (
            <div
              style={{
                marginTop: "30px",
                marginBottom: "20px",
              }}
              className="table-responsive"
            >
                <Table className="table-hover-effect table-interactive">
                <thead>
                  <tr>
                    <th style={StyleTableHeaders} className="text-cell">Folio</th>
                    <th style={StyleTableHeaders} className="text-cell">Fecha</th>
                    <th style={StyleTableHeaders} className="text-cell">Tickets</th> 
                    <th style={StyleTableHeaders} className="text-cell">Razón social</th>
                    <th style={StyleTableHeaders} className="money-cell">Subtotal</th>
                    <th style={StyleTableHeaders} className="money-cell">IEPS</th>
                    <th style={StyleTableHeaders} className="money-cell">IVA</th>
                    <th style={StyleTableHeaders} className="money-cell">Total</th>
                    <th style={StyleTableHeaders}>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.data?.results?.length > 0 ? (
                    invoices.data.results.map((item) => {
                      const invoiceState = item.invoice_state || "Pendiente"
                      const stateClass = `billing-row-${invoiceState.toLowerCase()}`
                      const isDownloadingPdf = downloadingInvoice?.id === item.id &&
                        downloadingInvoice?.type === "pdf"
                      const isDownloadingXml = downloadingInvoice?.id === item.id &&
                        downloadingInvoice?.type === "xml"
                      
                      return (
                      <tr key={item.id} className={stateClass}>
                        {(() => {
                          const isStamped = invoiceState === "Timbrada"
                          const isCancelled = invoiceState === "Cancelada"

                          return (
                            <>
                        {/* Folio */}
                        <td className="text-cell">
                          {isStamped ? (
                            <button
                              style={{
                                background: "none",
                                border: "none",
                                color: "#007bff",
                                cursor: "pointer",
                                padding: 0,
                              }}
                              onMouseEnter={(e) => {
                                e.target.style.textDecoration = "underline"
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.textDecoration = "none"
                              }}
                              onClick={() => handleViewInvoicePDF(item)}
                              disabled={Boolean(downloadingInvoice)}
                            >
                              {getInvoiceLabel(item)}
                            </button>
                          ) : (
                            getInvoiceLabel(item)
                          )}
                        </td>

                        {/* Fecha */}
                        <td  className="text-cell">
                          {item.arrival_date
                            ? formatDate(item.arrival_date)
                            : item.pay_date
                            ? formatDate(item.pay_date)
                            : "N/A"}
                        </td>

                        {/* Tickets */}
                        <td  className="text-cell">
                          <div style={{ fontSize: "0.85em" }}>
                            {Array.isArray(item.tickets) && item.tickets.length > 0 ? (
                              <div>
                                {item.tickets.map((ticketItem, index) => {
                                  const ticketNumber = ticketItem.number || ticketItem.folio || item.number_invoice
                                  const isLastInRow = (index + 1) % 3 === 0
                                  const isLast = index === item.tickets.length - 1

                                  return (
                                    <span key={ticketItem.id || `${item.id}-${index}`}>
                                      {ticketNumber ? (
                                        <button
                                          style={{
                                            background: "none",
                                            border: "none",
                                            color: "#007bff",
                                            textDecoration: "none",
                                            cursor: "pointer",
                                            padding: 0,
                                          }}
                                          onMouseEnter={(e) => {
                                            e.target.style.textDecoration = "underline"
                                          }}
                                          onMouseLeave={(e) => {
                                            e.target.style.textDecoration = "none"
                                          }}
                                          onClick={() =>
                                            handleViewTicket(ticketItem.id)
                                          }
                                        >
                                          {ticketNumber}
                                        </button>
                                      ) : null}
                                      {!isLast && ", "}
                                      {isLastInRow && !isLast && <br />}
                                    </span>
                                  )
                                })}
                              </div>
                            ) : item.number_invoice ? (
                              <button
                                style={{
                                  background: "none",
                                  border: "none",
                                  color: item?.tickets?.[0]?.id ? "#007bff" : "#6c757d",
                                  textDecoration: "none",
                                  cursor: item?.tickets?.[0]?.id ? "pointer" : "default",
                                  padding: 0,
                                }}
                                disabled={!item?.tickets?.[0]?.id}
                                onMouseEnter={(e) => {
                                  e.target.style.textDecoration = "underline"
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.textDecoration = "none"
                                }}
                                onClick={() =>
                                  handleViewTicket(item?.tickets?.[0]?.id)
                                }
                              >
                                {item.number_invoice}
                              </button>
                            ) : null}
                          </div>
                        </td>

                        {/* Razón social */}
                        <td className="text-cell">
                          {item.business_name || "N/A"}
                        </td>

                        {/* Subtotal */}
                        <td style={styleCenterTable} className="money-cell">
                          {item.amount
                            ? currencyFormatter({
                                currency: "USD",
                                value: item.amount,
                              })
                            : "N/A"}
                        </td>

                        {/* IEPS */}
                        <td style={styleCenterTable} className="money-cell">
                          {currencyFormatter({ currency: "USD", value: item.ieps_amount ?? 0 })}
                        </td>

                        {/* IVA */}
                        <td style={styleCenterTable} className="money-cell">
                          {currencyFormatter({ currency: "USD", value: item.iva_amount ?? item.tax ?? 0 })}
                        </td>

                        {/* Total */}
                        <td style={styleCenterTable} className="money-cell">
                          {item.total
                            ? currencyFormatter({
                                currency: "USD",
                                value: parseFloat(item.total),
                              })
                            : "N/A"}
                        </td>

                        {/* Acciones */}
                        <td style={styleCenterTable}>
                          <div style={{ display: "flex", gap: "5px", flexWrap: "wrap", justifyContent: "center" }}>
                            {/* Timbrar */}
                            {!isStamped && (
                              <Button
                                color="info"
                                size="sm"
                                onClick={() => handleTimbrado(item.id)}
                                title="Timbrar factura"
                              >
                                <i className="fa fa-stamp" />
                              </Button>
                            )}

                            {/* Descargar PDF */}
                            {isStamped && (
                              <Button
                                color="danger"
                                size="sm"
                                onClick={() => handleDownloadPDF(item.id)}
                                title="Descargar PDF"
                                disabled={Boolean(downloadingInvoice)}
                              >
                                <i className={isDownloadingPdf ? "fa fa-spinner fa-spin" : "fa fa-file-pdf"} />
                              </Button>
                            )}

                            {/* Descargar XML */}
                            {isStamped && (
                              <Button
                                color="warning"
                                size="sm"
                                onClick={() => handleDownloadXML(item.id)}
                                title="Descargar XML"
                                disabled={Boolean(downloadingInvoice)}
                              >
                                <i className={isDownloadingXml ? "fa fa-spinner fa-spin" : "fa fa-file-code"} />
                              </Button>
                            )}

                            {/* Enviar por correo */}
                            {isStamped && (
                              <Button
                                color="primary"
                                size="sm"
                                onClick={() => openSendMailModal(item)}
                                title="Enviar factura por correo"
                                disabled={!item?.tickets?.[0]?.id}
                              >
                                <i className="fa fa-paper-plane" />
                              </Button>
                            )}

                            {/* Descargar Acuse: sólo para facturas canceladas */}
                            {isCancelled && (
                              <Button
                                color="success"
                                size="sm"
                                onClick={() => handleGetAcuse(item.id)}
                                title="Descargar acuse SAT"
                              >
                                <i className="fa fa-check-square" />
                              </Button>
                            )}

                            {/* Cancelar */}
                            {!isCancelled && isStamped && (
                              <Button
                                color="outline-danger"
                                size="sm"
                                onClick={() => handleCancel(item.id)}
                                title="Cancelar factura"
                              >
                                <i className="fa fa-times" />
                              </Button>
                            )}
                          </div>
                        </td>
                            </>
                          )
                        })()}
                      </tr>
                    )
                    })
                  ) : (
                    <tr>
                      <td colSpan={9} className="text-center">
                        No hay datos
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          )}

          <div className="d-flex justify-content-between align-items-center mt-2">
            <div className="text-muted" style={{ fontSize: "0.9rem" }}>
              Mostrando {startItem}-{endItem} de {totalCount} facturas
            </div>
            <div className="d-flex align-items-center" style={{ gap: "8px" }}>
              <Button
                color="secondary"
                outline
                size="sm"
                disabled={currentPage <= 1 || isLoading || invoices.isFetching}
                onClick={() => handleChangePage(currentPage - 1)}
              >
                Anterior
              </Button>
              <span style={{ minWidth: "92px", textAlign: "center" }}>
                Página {currentPage} / {totalPages}
              </span>
              <Button
                color="secondary"
                outline
                size="sm"
                disabled={currentPage >= totalPages || isLoading || invoices.isFetching}
                onClick={() => handleChangePage(currentPage + 1)}
              >
                Siguiente
              </Button>
            </div>
          </div>
        </div>
      </Container>
    </div>
    </>
  )
}

export default BillingList
